import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { BusinessService } from '../../../services/business.service';
import { Role, Permission } from '../../../models/access.model';
import { BusinessModel, BusinessProfileViewModel, TerminalListItem } from '../../../models/user.model';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BusinessFeeConfig, UpsertBusinessFeeConfigRequest } from '../../../models/business-fee-config.model';
import { BusinessModeOption } from '../../../models/business-mode.model';
import {
  BusinessDeliveryConfig,
  BusinessDeliveryOptionConfig,
  UpdateBusinessDeliveryConfigRequest
} from '../../../models/business-delivery-config.model';
import { SubscriptionSnippet } from '../../../models/subscription.model';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../../services/auth/auth.service';
import { PlanService } from '../../../services/plan.service';
import { GeographyService } from '../../../services/geography.service';
import { Plan, AssignPlanRequest, BusinessAddOn, PlanCatalogEntry } from '../../../models/plan.model';
import { Country } from '../../../models/geography.model';
import {
  terminalStatusClass as resolveTerminalStatusClass,
  terminalStatusLabel as resolveTerminalStatusLabel,
} from '../../../shared/terminal-liveness';

@Component({
  selector: 'app-business-profile',
  standalone: false,
  templateUrl: './business-profile.component.html',
  styleUrls: ['./business-profile.component.css']
})
export class BusinessProfileComponent implements OnChanges {
  readonly staffAuth = inject(AuthService);
  isLoading = false;
  @Input() businessId?: string;

  /**
   * Edit mode. The profile opens view-only; the edit bar takes this business's edit lease, so
   * only one admin at a time can change it. Re-emitted for a host page with edit controls of
   * its own (the Businesses page's test-business switch).
   */
  editing = false;
  @Output() editingChange = new EventEmitter<boolean>();
  business: BusinessModel | null = null;
  businessDetails: BusinessProfileViewModel | null = null;
  businessUsers: any[] = [];
  businessRoles: Role[] = [];
  businessPermissions: Permission[] = [];
  subscription: SubscriptionSnippet | null = null;
  subscriptionLoading = false;
  subscriptionError = '';
  showBillingDetails = false;
  private readonly locale = inject(LOCALE_ID);

  // Plan assignment / trial setup (admin)
  get canManagePlans(): boolean { return this.authService.can('Businesses.Entitlements.Manage'); }
  /** The owner links to /users/:userId, which needs Platform.Users.View. */
  get canOpenUsers(): boolean { return this.authService.can('Platform.Users.View'); }
  plans: Plan[] = [];
  countries: Country[] = [];
  showAssignPlan = false;
  assignSaving = false;
  assignError = '';
  assignMessage = '';
  assignForm: FormGroup;

  /**
   * Modules (admin). A module is an add-on plan with a merchant-app catalog row; it sits
   * beside the owner's main plan, so it has its own list and its own grant/cancel:
   * assigning one through "Assign plan" would REPLACE the main plan, which is the
   * opposite of what a module is for. Plain add-ons without a catalog row list here too.
   */
  addOns: BusinessAddOn[] = [];
  addOnsLoading = false;
  addOnsError = '';
  addOnPlans: Plan[] = [];
  /** planId -> module catalog row (display name, sort order). Empty if the catalog fails. */
  private modulesByPlanId = new Map<number, PlanCatalogEntry>();
  grantAddOnPlanId: number | null = null;
  grantAddOnEndsAt: string | null = null;
  addOnSaving = false;
  addOnMessage = '';
  addOnBusyId: string | null = null;

  /**
   * Operating mode (admin). It moved here from the merchant's own settings:
   * mode decides the landing destination and the checkout steps, so changing it
   * after go-live rewrites how the register behaves for everyone standing at it.
   * The setup wizard sets it once, at creation, when nothing depends on it yet;
   * after that it is a support action, requested rather than self-served.
   */
  modes: BusinessModeOption[] = [];
  selectedModeId: number | null = null;
  modeSaving = false;
  modeError = '';
  modeMessage = '';

  deliveryConfig: BusinessDeliveryConfig | null = null;
  deliveryIsLoading = false;
  deliveryIsSaving = false;
  deliveryError = '';
  deliverySaveMessage = '';

  readonly DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /** Zones offered in the picker; falls back to a short list where Intl cannot enumerate them. */
  readonly timezones: string[] = BusinessProfileComponent.listTimezones();

  /** New-slot inputs, keyed by deliveryTypeId so each option has its own row. */
  newSlotDay: Record<number, number | ''> = {};
  newSlotStart: Record<number, string> = {};
  newSlotEnd: Record<number, string> = {};
  newSlotMaxOrders: Record<number, number | null> = {};

  feeConfig: BusinessFeeConfig | null = null;
  feeIsLoading = false;
  feeIsSaving = false;
  feeError = '';
  feeSaveMessage = '';
  get canManageFees(): boolean { return this.authService.can('Businesses.Entitlements.Manage'); }
  get canViewFees(): boolean { return this.authService.can('Businesses.View'); }

  // Terminals
  terminals: TerminalListItem[] = [];
  terminalsLoading = false;
  terminalsError = '';
  get canManageTerminals(): boolean { return this.authService.can('Businesses.Operate'); }
  showAddTerminal = false;
  savingTerminal = false;
  terminalCreateError = '';
  terminalNameControl = new FormControl<string>('', { nonNullable: true });
  togglingTerminalId: string | null = null;
  terminalStatusError = '';

  feeForm: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private businessService: BusinessService,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private planService: PlanService,
    private geographyService: GeographyService
  ) {
    this.assignForm = this.formBuilder.group({
      planId: this.formBuilder.control<number | null>(null, [Validators.required]),
      billingCycle: this.formBuilder.control<string>('Monthly', { nonNullable: true }),
      countryCode: this.formBuilder.control<string | null>(null),
      mode: this.formBuilder.control<'active' | 'trial'>('trial', { nonNullable: true }),
      // Friendly trial controls: pick a length preset (days) or a custom end date.
      trialLengthDays: this.formBuilder.control<number>(14, { nonNullable: true }),
      customTrialEndsAt: this.formBuilder.control<string | null>(null),
      autoBill: this.formBuilder.control<boolean>(false, { nonNullable: true })
    });
    this.feeForm = this.formBuilder.group(
      {
        sellerCommissionRatePct: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        customerFeeRatePct: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        sellerCommissionMinAmount: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        sellerCommissionMaxAmount: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        customerFeeMinAmount: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        customerFeeMaxAmount: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0)]),
        effectiveFrom: this.formBuilder.control<string>('', [Validators.required]),
        effectiveTo: this.formBuilder.control<string | null>(null)
      },
      {
        validators: [
          this.minLessThanOrEqualMaxValidator('sellerCommissionMinAmount', 'sellerCommissionMaxAmount', 'sellerRangeInvalid'),
          this.minLessThanOrEqualMaxValidator('customerFeeMinAmount', 'customerFeeMaxAmount', 'customerRangeInvalid'),
          this.effectiveDateRangeValidator()
        ]
      }
    );
    this.applyFeeFormAccess();
    this.resetFeeFormToDefaults();
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.businessId = id;
        // A different business opens view-only; the edit bar gives back the previous lease.
        this.editing = false;
        this.loadBusiness();
      }
    });
    if (this.canManagePlans) {
      this.loadPlansAndCountries();
    }
    if (this.authService.can('Businesses.Operate')) {
      this.businessService.getBusinessModes().subscribe({
        next: modes => (this.modes = modes || []),
        error: () => { /* keep empty; the picker shows nothing to choose */ }
      });
    }
  }

  /**
   * Apply the mode. Null clears the assignment, so mode derives from business
   * type again — a real choice, not a missing value, which is why the picker
   * carries an explicit "derive from business type" option.
   */
  saveBusinessMode(): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    if (!this.businessId || this.modeSaving || !this.authService.can('Businesses.Operate')) { return; }

    this.modeSaving = true;
    this.modeError = '';
    this.modeMessage = '';

    this.businessService.setBusinessMode(this.businessId, this.selectedModeId).subscribe({
      next: () => {
        this.modeSaving = false;
        // Terminals cache the profile; the server evicts it, but the registers
        // pick the change up on their next fetch rather than instantly.
        this.modeMessage = 'Mode updated. Registers pick it up on their next refresh.';
      },
      error: (err) => {
        this.modeSaving = false;
        this.modeError = err?.error?.message || 'Could not change the mode. Please try again.';
      }
    });
  }

  private loadPlansAndCountries(): void {
    this.planService.getPlans().subscribe({
      next: (plans) => {
        const active = (plans || []).filter(p => p.isActive);
        // The assign-plan picker offers main plans only; add-ons have their own panel.
        this.plans = active.filter(p => p.planKind !== 'addon');
        this.addOnPlans = active.filter(p => p.planKind === 'addon');
      },
      error: () => { /* keep empty; assign UI will show no plans */ }
    });
    // Module names for the Modules panel. Without it, plans show by plan name.
    this.planService.getCatalog().subscribe({
      next: (rows) => {
        this.modulesByPlanId = new Map(
          rows.filter(r => r.planId !== null).map(r => [r.planId as number, r])
        );
      },
      error: () => (this.modulesByPlanId = new Map())
    });
    this.geographyService.getCountries().subscribe(countries => (this.countries = countries || []));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId'] && this.businessId) {
      // A different business opens view-only; the edit bar gives back the previous lease.
      this.editing = false;
      this.loadBusiness();
    }
  }

  /** The edit bar took or gave back the lease. Leaving edit mode closes anything half-done. */
  onEditingChange(editing: boolean): void {
    this.editing = editing;
    if (!editing) {
      if (this.showAssignPlan) this.cancelAssignPlan();
      if (this.showAddTerminal) this.cancelAddTerminal();
    }
    this.editingChange.emit(editing);
  }

  private loadBusiness() {
    this.isLoading = true;
    // Load base list and find selected business name if needed
    if (this.businessId) {
      this.businessService.getBusinesses().subscribe(list => {
        const all = list || [];
        this.business = all.find(b => String(b.businessId) === String(this.businessId)) || null;
      });
    }

    const id = String(this.businessId);
    this.businessService.getBusinessDetails(id).subscribe(d => this.businessDetails = d);
    this.businessService.getBusinessUsers(id).subscribe(users => this.businessUsers = users || []);
    this.loadSubscription(id);
    this.loadAddOns(id);
    this.loadTerminals(id);
    if (this.canViewFees) {
      this.loadBusinessFeeConfig(id);
    }
    this.loadDeliveryConfig(id);
    this.businessService.getBusinessRolesPermissions(id).subscribe(rp => {
      this.businessRoles = rp?.roles || [];
      const list = (rp?.permissions || []) as any[];
      this.businessPermissions = list.map((p: any) => ({
        permissionId: p.permissionId ?? p.code,
        code: p.code,
        name: p.name,
        category: p.category,
        description: p.description,
        grantedByRoles: p.grantedByRoles ?? ''
      }));
    }).add(() => this.isLoading = false);
  }

  private loadAddOns(businessId: string): void {
    this.addOnsLoading = true;
    this.addOnsError = '';
    this.businessService.getBusinessAddOns(businessId).subscribe({
      next: (addOns) => (this.addOns = addOns || []),
      error: (error: HttpErrorResponse) => {
        this.addOns = [];
        // 404 = no resolvable owner, which the subscription panel already explains.
        if (error.status !== 404) {
          this.addOnsError = 'Failed to load modules.';
        }
      }
    }).add(() => (this.addOnsLoading = false));
  }

  get liveAddOns(): BusinessAddOn[] {
    return this.addOns.filter(a => a.isActive);
  }

  /** Module display name from the catalog, falling back to the plan name. */
  moduleName(planId: number, planName: string): string {
    return this.modulesByPlanId.get(planId)?.displayName || planName;
  }

  /** Purchased through checkout, or comped by an admin (IsGranted: nothing billed). */
  moduleSource(addOn: BusinessAddOn): 'Purchased' | 'Comped' {
    return addOn.isGranted ? 'Comped' : 'Purchased';
  }

  /**
   * What the comp picker offers: modules the owner does not already hold live (catalog
   * order), then any plain add-ons. Held ones are listed above, not offered.
   */
  get grantableAddOnPlans(): { planId: number; label: string }[] {
    const held = new Set(this.liveAddOns.map(a => a.planId));
    const open = this.addOnPlans.filter(p => !held.has(p.planID));
    const order = (p: Plan) => this.modulesByPlanId.get(p.planID)?.sortOrder ?? 0;
    const modules = open
      .filter(p => this.modulesByPlanId.has(p.planID))
      .sort((a, b) => order(a) - order(b))
      .map(p => ({ planId: p.planID, label: this.moduleName(p.planID, p.planName) }));
    const others = open
      .filter(p => !this.modulesByPlanId.has(p.planID))
      .map(p => ({ planId: p.planID, label: p.planName + ' (add-on)' }));
    return [...modules, ...others];
  }

  grantAddOn(): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    if (!window.confirm('Grant this add-on without collecting payment?')) return;
    if (!this.businessId || this.addOnSaving || !this.canManagePlans || !this.grantAddOnPlanId) {
      return;
    }
    this.addOnSaving = true;
    this.addOnsError = '';
    this.addOnMessage = '';
    const id = String(this.businessId);
    this.businessService.grantBusinessAddOn(id, {
      planId: Number(this.grantAddOnPlanId),
      endsAt: this.grantAddOnEndsAt || null
    }).subscribe({
      next: (response) => {
        this.addOns = response?.addOns || this.addOns;
        this.addOnMessage = 'Module comped. Nothing is billed; cancel it here when it should stop.';
        this.grantAddOnPlanId = null;
        this.grantAddOnEndsAt = null;
        if (!response?.addOns) {
          this.loadAddOns(id);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.addOnsError = error?.error?.message || 'Failed to grant the module.';
      }
    }).add(() => (this.addOnSaving = false));
  }

  cancelAddOn(addOn: BusinessAddOn): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    if (!this.businessId || this.addOnBusyId || !this.canManagePlans) {
      return;
    }
    const name = this.moduleName(addOn.planId, addOn.planName);
    const question = 'Cancel ' + name + " for this business's owner? What it unlocks stops straight away. Provider billing, if any, must be cancelled separately.";
    if (!confirm(question)) {
      return;
    }
    this.addOnBusyId = addOn.userPlanAddOnId;
    this.addOnsError = '';
    this.addOnMessage = '';
    const id = String(this.businessId);
    this.businessService.cancelBusinessAddOn(id, addOn.userPlanAddOnId).subscribe({
      next: (response) => {
        this.addOns = response?.addOns || this.addOns.filter(a => a.userPlanAddOnId !== addOn.userPlanAddOnId);
        this.addOnMessage = name + ' cancelled.';
      },
      error: (error: HttpErrorResponse) => {
        this.addOnsError = error?.error?.message || 'Failed to cancel the module.';
      }
    }).add(() => (this.addOnBusyId = null));
  }

  /**
   * The business's account owner - the user whose plan the business is billed under. The
   * profile's account holder is the API's resolution (owner, else the member holding a plan);
   * the list's owner (dbo.BusinessOwners) fills in until the profile has loaded.
   */
  get ownerUserId(): string | null {
    return this.businessDetails?.accountHolder?.userId || this.business?.ownerUserId || null;
  }

  get ownerName(): string | null {
    return this.businessDetails?.accountHolder?.name || this.business?.ownerName || null;
  }

  get ownerEmail(): string | null {
    return this.businessDetails?.accountHolder?.email || null;
  }

  private loadSubscription(businessId: string): void {
    this.subscriptionLoading = true;
    this.subscriptionError = '';
    this.subscription = null;
    this.showBillingDetails = false;
    this.businessService.getBusinessSubscription(businessId).subscribe({
      next: (snippet) => {
        this.subscription = snippet;
      },
      error: (error: HttpErrorResponse) => {
        this.subscription = null;
        // A 404 simply means the business has no resolvable subscription.
        if (error.status !== 404) {
          this.subscriptionError = 'Failed to load subscription.';
        }
      }
    }).add(() => this.subscriptionLoading = false);
  }

  get isTrialMode(): boolean {
    return this.assignForm?.get('mode')?.value === 'trial';
  }

  /**
   * Effective status of the loaded subscription, factoring the reported
   * status together with the start/end dates. The backend flags can lag
   * (e.g. an expired trial still marked Active), so dates win.
   */
  get subscriptionStatus(): { label: string; active: boolean } | null {
    const s = this.subscription;
    if (!s) {
      return null;
    }

    const now = new Date();
    const raw = (s.status || '').trim().toLowerCase();

    // Terminal statuses reported by the backend always win.
    if (raw === 'cancelled' || raw === 'canceled') {
      return { label: 'Cancelled', active: false };
    }
    if (raw === 'suspended') {
      return { label: 'Suspended', active: false };
    }
    if (raw === 'expired') {
      return { label: 'Expired', active: false };
    }

    const start = this.parseSubscriptionDate(s.startDate);
    if (start && start > now) {
      return { label: 'Not started', active: false };
    }

    const end = this.parseSubscriptionDate(s.endDate, true);
    if (s.isTrial) {
      const trialEnd = this.parseSubscriptionDate(s.trialEndsAt, true) || end;
      if (trialEnd && trialEnd < now) {
        return { label: 'Trial expired', active: false };
      }
      return { label: 'Trial', active: true };
    }

    if (end && end < now) {
      return { label: 'Expired', active: false };
    }

    if (s.isActive || raw === 'active') {
      return { label: 'Active', active: true };
    }
    return { label: s.status || 'Inactive', active: false };
  }

  /**
   * The account holder's billing address on one line, skipping the parts that aren't filled
   * in. Empty string when there's nothing to show, so the row can be hidden.
   */
  get billingAddressLine(): string {
    const s = this.subscription;
    if (!s) {
      return '';
    }
    return [s.billingAddress, s.billingCity, s.billingState, s.billingPostalCode, s.billingCountryName]
      .filter(part => !!part && `${part}`.trim().length > 0)
      .join(', ');
  }

  /** Billing contact name, falling back to the person's own name when no trading name is set. */
  get billingContactName(): string {
    const s = this.subscription;
    if (!s) {
      return '';
    }
    return (s.billingName || `${s.billingFirstName || ''} ${s.billingLastName || ''}`).trim();
  }

  /**
   * The one date that matters next, phrased for it: "Renews Nov 21, 2026", "Ends …",
   * "Ended …" or "Trial ends …". Falls back to the processor's next payment date when the
   * plan has no end date (open-ended paid plans).
   */
  get subscriptionTimelineLabel(): string {
    const s = this.subscription;
    if (!s) {
      return '';
    }
    const end = this.parseSubscriptionDate(s.isTrial ? (s.trialEndsAt || s.endDate) : s.endDate, true);
    const next = this.parseSubscriptionDate(s.nextPaymentDate);
    const date = end ?? next;
    if (!date) {
      return '';
    }
    const formatted = formatDate(date, 'mediumDate', this.locale);
    if (date < new Date()) {
      return `${s.isTrial ? 'Trial ended' : 'Ended'} ${formatted}`;
    }
    if (s.isTrial) {
      return `Trial ends ${formatted}`;
    }
    return `${s.autoRenewal || (!end && next) ? 'Renews' : 'Ends'} ${formatted}`;
  }

  /** One sentence per plan limit the account is over, e.g. "8 businesses on a 2-business plan". */
  get subscriptionLimitWarnings(): string[] {
    const s = this.subscription;
    if (!s) {
      return [];
    }
    const warnings: string[] = [];
    if (this.isOverLimit(s.currentBusinesses, s.maxBusinesses)) {
      warnings.push(`${s.currentBusinesses} businesses on a ${s.maxBusinesses}-business plan`);
    }
    if (this.isOverLimit(s.currentTerminals, s.maxTerminals)) {
      warnings.push(`${s.currentTerminals} terminals on a ${s.maxTerminals}-terminal plan`);
    }
    return warnings;
  }

  isOverLimit(current: number | null | undefined, max: number | null | undefined): boolean {
    return max != null && current != null && current > max;
  }

  /**
   * Parse a subscription date. Date-only values (yyyy-MM-dd) are treated as
   * inclusive when used as an end boundary, i.e. the subscription remains
   * valid through the whole of that day.
   */
  private parseSubscriptionDate(value: string | null, endOfDay = false): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    if (endOfDay && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  }

  /** Friendly one-step trial setup. */
  startTrial(): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    this.showAssignPlan = true;
    this.assignError = '';
    this.assignMessage = '';
    this.assignForm.reset({
      planId: this.plans.length === 1 ? this.plans[0].planID : (this.subscription?.planId ?? null),
      billingCycle: 'Monthly',
      countryCode: null,
      mode: 'trial',
      trialLengthDays: 14,
      customTrialEndsAt: null,
      autoBill: false
    });
  }

  /** Advanced: assign a paid plan directly (no trial). */
  startAssignPlan(): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    this.showAssignPlan = true;
    this.assignError = '';
    this.assignMessage = '';
    this.assignForm.reset({
      planId: this.subscription?.planId ?? null,
      billingCycle: this.subscription?.billingInterval || 'Monthly',
      countryCode: null,
      mode: 'active',
      trialLengthDays: 14,
      customTrialEndsAt: null,
      autoBill: false
    });
  }

  cancelAssignPlan(): void {
    this.showAssignPlan = false;
    this.assignSaving = false;
    this.assignError = '';
  }

  /** Resolve the trial end date from the friendly length preset or a custom date. */
  private resolveTrialEndsAt(value: { trialLengthDays: number; customTrialEndsAt: string | null }): string | null {
    if (value.customTrialEndsAt) {
      return value.customTrialEndsAt;
    }
    const days = Number(value.trialLengthDays);
    if (!days || days <= 0) {
      return null; // let the backend apply its default trial length
    }
    const end = new Date();
    end.setDate(end.getDate() + days);
    return end.toISOString().slice(0, 10);
  }

  submitAssignPlan(): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    if (!window.confirm('Apply this plan or trial without collecting payment?')) return;
    if (!this.businessId || this.assignSaving || !this.canManagePlans) {
      return;
    }

    this.assignForm.markAllAsTouched();
    if (this.assignForm.invalid) {
      return;
    }

    const value = this.assignForm.getRawValue();
    const isTrial = value.mode === 'trial';
    const request: AssignPlanRequest = {
      planId: Number(value.planId),
      billingCycle: value.billingCycle,
      countryCode: value.countryCode || null,
      mode: value.mode,
      trialEndsAt: isTrial ? this.resolveTrialEndsAt(value) : null,
      conversionMode: isTrial ? (value.autoBill ? 'auto' : 'manual') : null
    };

    this.assignSaving = true;
    this.assignError = '';
    this.assignMessage = '';

    const id = String(this.businessId);
    this.businessService.assignBusinessPlan(id, request).subscribe({
      next: (response) => {
        this.showAssignPlan = false;
        const processor = response?.resolvedProcessor
          ? ` Payment processor: ${response.resolvedProcessor}.`
          : '';
        this.assignMessage = `${request.mode === 'trial' ? 'Trial started' : 'Plan assigned'}.` + processor;
        // Refresh the snippet from the response when available, else reload.
        if (response?.overview) {
          this.subscription = response.overview as SubscriptionSnippet;
        } else {
          this.loadSubscription(id);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.assignError = error?.error?.message || 'Failed to assign plan.';
      }
    }).add(() => (this.assignSaving = false));
  }

  private loadTerminals(businessId: string): void {
    this.terminalsLoading = true;
    this.terminalsError = '';
    this.businessService.getBusinessTerminals(businessId).subscribe({
      next: (terminals) => {
        this.terminals = terminals || [];
      },
      error: () => {
        this.terminals = [];
        this.terminalsError = 'Failed to load terminals.';
      }
    }).add(() => this.terminalsLoading = false);
  }

  startAddTerminal(): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    this.showAddTerminal = true;
    this.terminalCreateError = '';
    this.terminalNameControl.setValue('');
  }

  cancelAddTerminal(): void {
    this.showAddTerminal = false;
    this.savingTerminal = false;
    this.terminalCreateError = '';
    this.terminalNameControl.setValue('');
  }

  saveTerminal(): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    if (!this.businessId || this.savingTerminal || !this.canManageTerminals) {
      return;
    }

    this.savingTerminal = true;
    this.terminalCreateError = '';

    const id = String(this.businessId);
    const name = this.terminalNameControl.value?.trim() || null;

    this.businessService.createBusinessTerminal(id, name).subscribe({
      next: () => {
        this.showAddTerminal = false;
        this.terminalNameControl.setValue('');
        this.loadTerminals(id);
      },
      error: (error: HttpErrorResponse) => {
        this.terminalCreateError =
          error?.error?.message || 'Failed to create terminal.';
      }
    }).add(() => this.savingTerminal = false);
  }

  setTerminalActive(terminal: TerminalListItem, isActive: boolean): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    if (!this.businessId || !this.canManageTerminals || this.togglingTerminalId) {
      return;
    }

    const id = String(this.businessId);
    this.togglingTerminalId = terminal.terminalId;
    this.terminalStatusError = '';

    this.businessService.setBusinessTerminalActive(id, terminal.terminalId, isActive).subscribe({
      next: () => {
        terminal.isActive = isActive;
      },
      error: (error: HttpErrorResponse) => {
        this.terminalStatusError =
          error?.error?.message || `Failed to ${isActive ? 'enable' : 'disable'} terminal.`;
      }
    }).add(() => this.togglingTerminalId = null);
  }

  /**
   * Connectivity as resolved by the server. Rendered through the shared helpers so this panel and
   * the platform-wide Terminals screen describe the same terminal the same way.
   *
   * Kept separate from `isActive` and `offlineMode`: a disabled terminal can still be reachable,
   * and one prepared for offline trading is expected to go quiet, so collapsing the three into a
   * single status would make each of them unreadable.
   */
  terminalStatusLabel(terminal: TerminalListItem): string {
    return resolveTerminalStatusLabel(terminal.status);
  }

  terminalStatusClass(terminal: TerminalListItem): string {
    return resolveTerminalStatusClass(terminal.status);
  }

  private loadDeliveryConfig(businessId: string): void {
    this.deliveryIsLoading = true;
    this.deliveryError = '';
    this.deliverySaveMessage = '';

    this.businessService.getBusinessDeliveryConfig(businessId).subscribe({
      next: (config) => {
        this.deliveryConfig = config;
        (config?.options || []).forEach(option => this.initSlotInputs(option.deliveryTypeId));
      },
      error: () => {
        this.deliveryConfig = null;
        this.deliveryError = 'Failed to load delivery configuration.';
      }
    }).add(() => this.deliveryIsLoading = false);
  }

  private initSlotInputs(deliveryTypeId: number): void {
    if (!(deliveryTypeId in this.newSlotDay)) this.newSlotDay[deliveryTypeId] = '';
    if (!(deliveryTypeId in this.newSlotStart)) this.newSlotStart[deliveryTypeId] = '';
    if (!(deliveryTypeId in this.newSlotEnd)) this.newSlotEnd[deliveryTypeId] = '';
    if (!(deliveryTypeId in this.newSlotMaxOrders)) this.newSlotMaxOrders[deliveryTypeId] = null;
  }

  formatDayName(dayOfWeek: number | null): string {
    return dayOfWeek === null || dayOfWeek < 0 || dayOfWeek > 6 ? '—' : this.DAY_NAMES[dayOfWeek];
  }

  /** "07:00:00" → "07:00" for display and for the time inputs. */
  formatTime(value: string): string {
    return (value || '').slice(0, 5);
  }

  addTimeSlot(option: BusinessDeliveryOptionConfig): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    const id = option.deliveryTypeId;
    const day = this.newSlotDay[id];
    const start = this.newSlotStart[id];
    const end = this.newSlotEnd[id];

    if (day === '' || day === null || day === undefined || !start || !end) {
      this.deliveryError = 'Pick a day, a start time and an end time before adding a window.';
      return;
    }

    if (this.normalizeTime(end) <= this.normalizeTime(start)) {
      this.deliveryError = 'A window must end after it starts.';
      return;
    }

    this.deliveryError = '';
    option.timeSlots = [
      ...(option.timeSlots || []),
      {
        businessPickupTimeSlotId: 0,
        businessDeliveryTypeId: option.businessDeliveryTypeId,
        dayOfWeek: Number(day),
        startTime: this.normalizeTime(start),
        endTime: this.normalizeTime(end),
        maxOrders: this.newSlotMaxOrders[id] ?? null,
        isActive: true
      }
    ];

    this.newSlotDay[id] = '';
    this.newSlotStart[id] = '';
    this.newSlotEnd[id] = '';
    this.newSlotMaxOrders[id] = null;
  }

  removeTimeSlot(option: BusinessDeliveryOptionConfig, index: number): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    option.timeSlots = (option.timeSlots || []).filter((_, i) => i !== index);
  }

  saveDeliveryConfig(): void {
    if (!this.staffAuth.can('Businesses.Operate')) return;
    if (!this.businessId || !this.deliveryConfig || this.deliveryIsSaving) {
      return;
    }

    const id = String(this.businessId);

    // Every option the seller should keep has to be in the payload: the API deactivates any
    // delivery type it does not receive.
    const payload: UpdateBusinessDeliveryConfigRequest = {
      timezone: this.deliveryConfig.timezone?.trim() || null,
      options: (this.deliveryConfig.options || [])
        .filter(option => option.isActive)
        .map(option => ({
          deliveryTypeId: option.deliveryTypeId,
          price: option.price ?? null,
          timeSlots: (option.timeSlots || []).map(slot => ({
            dayOfWeek: slot.dayOfWeek,
            startTime: this.normalizeTime(slot.startTime),
            endTime: this.normalizeTime(slot.endTime),
            maxOrders: slot.maxOrders ?? null
          }))
        }))
    };

    this.deliveryIsSaving = true;
    this.deliveryError = '';
    this.deliverySaveMessage = '';

    this.businessService.updateBusinessDeliveryConfig(id, payload).subscribe({
      next: (message) => {
        this.deliverySaveMessage = message || 'Delivery configuration saved.';
        this.loadDeliveryConfig(id);
      },
      error: () => {
        this.deliveryError = 'Failed to save delivery configuration.';
      }
    }).add(() => this.deliveryIsSaving = false);
  }

  /** Time inputs yield "HH:mm"; the API parses a TimeSpan, so send "HH:mm:ss". */
  private normalizeTime(value: string): string {
    const trimmed = (value || '').trim();
    return /^\d{1,2}:\d{2}$/.test(trimmed) ? `${trimmed}:00` : trimmed;
  }

  private static listTimezones(): string[] {
    const supported = (Intl as any)?.supportedValuesOf;
    if (typeof supported === 'function') {
      try {
        return supported.call(Intl, 'timeZone') as string[];
      } catch {
        // fall through to the short list
      }
    }

    return ['Africa/Accra', 'Africa/Lagos', 'Europe/London', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC'];
  }

  saveFeeConfig(): void {
    if (!this.staffAuth.can('Businesses.Entitlements.Manage')) return;
    if (!this.businessId || this.feeIsSaving || !this.canManageFees) {
      return;
    }

    this.feeForm.markAllAsTouched();
    if (this.feeForm.invalid) {
      return;
    }

    const value = this.feeForm.getRawValue();
    if (
      value.sellerCommissionRatePct === null
      || value.customerFeeRatePct === null
      || value.sellerCommissionMinAmount === null
      || value.sellerCommissionMaxAmount === null
      || value.customerFeeMinAmount === null
      || value.customerFeeMaxAmount === null
      || !value.effectiveFrom
    ) {
      return;
    }

    const id = String(this.businessId);

    const payload: UpsertBusinessFeeConfigRequest = {
      businessId: id,
      sellerCommissionRatePct: value.sellerCommissionRatePct,
      customerFeeRatePct: value.customerFeeRatePct,
      sellerCommissionMinAmount: value.sellerCommissionMinAmount,
      sellerCommissionMaxAmount: value.sellerCommissionMaxAmount,
      customerFeeMinAmount: value.customerFeeMinAmount,
      customerFeeMaxAmount: value.customerFeeMaxAmount,
      effectiveFrom: value.effectiveFrom,
      effectiveTo: value.effectiveTo || null
    };

    this.feeIsSaving = true;
    this.feeError = '';
    this.feeSaveMessage = '';

    const save$ = this.feeConfig?.businessFeeConfigId
      ? this.businessService.updateBusinessFeeConfig(this.feeConfig.businessFeeConfigId, payload)
      : this.businessService.createBusinessFeeConfig(payload);

    save$.subscribe({
      next: (message) => {
        this.feeSaveMessage = message || 'Fee configuration saved.';
        this.loadBusinessFeeConfig(id);
      },
      error: () => {
        this.feeError = 'Failed to save fee configuration.';
      }
    }).add(() => this.feeIsSaving = false);
  }

  private loadBusinessFeeConfig(businessId: string): void {
    this.feeIsLoading = true;
    this.feeError = '';
    this.feeSaveMessage = '';

    this.businessService.getBusinessFeeConfig(businessId).subscribe({
      next: (config) => {
        this.feeConfig = config;
        this.patchFeeForm(config);
      },
      error: (error: HttpErrorResponse) => {
        this.feeConfig = null;
        this.resetFeeFormToDefaults();
        if (error.status !== 404) {
          this.feeError = 'Failed to load fee configuration.';
        }
      }
    }).add(() => this.feeIsLoading = false);
  }

  private patchFeeForm(config: BusinessFeeConfig): void {
    this.feeForm.patchValue({
      sellerCommissionRatePct: config.sellerCommissionRatePct,
      customerFeeRatePct: config.customerFeeRatePct,
      sellerCommissionMinAmount: config.sellerCommissionMinAmount,
      sellerCommissionMaxAmount: config.sellerCommissionMaxAmount,
      customerFeeMinAmount: config.customerFeeMinAmount,
      customerFeeMaxAmount: config.customerFeeMaxAmount,
      effectiveFrom: this.toDateInputValue(config.effectiveFrom),
      effectiveTo: this.toDateInputValue(config.effectiveTo)
    });
    this.applyFeeFormAccess();
  }

  private resetFeeFormToDefaults(): void {
    this.feeForm.reset({
      sellerCommissionRatePct: null,
      customerFeeRatePct: null,
      sellerCommissionMinAmount: null,
      sellerCommissionMaxAmount: null,
      customerFeeMinAmount: null,
      customerFeeMaxAmount: null,
      effectiveFrom: this.todayDateInputValue(),
      effectiveTo: null
    });
    this.applyFeeFormAccess();
  }

  private applyFeeFormAccess(): void {
    if (this.canManageFees) {
      this.feeForm.enable({ emitEvent: false });
      return;
    }
    this.feeForm.disable({ emitEvent: false });
  }

  private toDateInputValue(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().slice(0, 10);
  }

  private todayDateInputValue(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private minLessThanOrEqualMaxValidator(minKey: string, maxKey: string, errorKey: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const min = control.get(minKey)?.value as number | null;
      const max = control.get(maxKey)?.value as number | null;
      if (min === null || max === null) {
        return null;
      }
      return min <= max ? null : { [errorKey]: true };
    };
  }

  private effectiveDateRangeValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const effectiveFrom = control.get('effectiveFrom')?.value as string | null;
      const effectiveTo = control.get('effectiveTo')?.value as string | null;
      if (!effectiveFrom || !effectiveTo) {
        return null;
      }
      const from = new Date(effectiveFrom);
      const to = new Date(effectiveTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return null;
      }
      return to >= from ? null : { effectiveDateInvalid: true };
    };
  }
}
